import { FORWARDED_URL_PARAMS } from "./constants";

export type ForwardedParams = Record<string, string>;

/**
 * Root-relative hrefs only. Protocol-relative (`//`), absolute (`http(s):`,
 * `mailto:`), and placeholder (`#`) links are treated as external and left
 * untouched so we never leak forwarded params onto third-party domains.
 */
export function isInternalHref(href: string): boolean {
	if (!href) return false;
	if (href.startsWith("//")) return false;
	return href.startsWith("/");
}

export function pickAllowlisted(
	source:
		| URLSearchParams
		| Record<string, string | Array<string> | undefined>,
	allowlist: ReadonlyArray<string> = FORWARDED_URL_PARAMS
): ForwardedParams {
	const out: ForwardedParams = {};
	for (const key of allowlist) {
		let value: string | undefined;
		if (source instanceof URLSearchParams) {
			value = source.get(key) ?? undefined;
		} else {
			const raw = source[key];
			value = Array.isArray(raw) ? raw[0] : raw;
		}
		if (value !== undefined && value !== "") {
			out[key] = value;
		}
	}
	return out;
}

export function mergeParams(
	base: ForwardedParams,
	override: ForwardedParams
): ForwardedParams {
	return { ...base, ...override };
}

/**
 * Append `params` to an internal href, overriding any same-named param the
 * CMS author baked in. External/placeholder hrefs are returned unchanged.
 */
export function appendForwardedParams(
	href: string,
	params: ForwardedParams
): string {
	if (!isInternalHref(href)) return href;
	if (Object.keys(params).length === 0) return href;

	const url = new URL(href, "https://__local__");
	for (const [key, value] of Object.entries(params)) {
		url.searchParams.set(key, value);
	}
	return `${url.pathname}${url.search}${url.hash}`;
}
